import { useHandler } from "@redotech/react-util/hook";
import type { TElement, Value } from "platejs";
import { createSlateEditor, deserializeHtml } from "platejs";
import { usePlateEditor } from "platejs/react";
import { serializeHtml } from "platejs/static";
import {
  type EditorToolbarMode,
  emailBasePlugins,
  getBasePluginsForMode,
  getPluginsForMode,
} from "./editor-plugins";

const createEmptyValue = (): Value => [
  { type: "p", children: [{ text: "" }] } as TElement,
];

export function htmlToPlateContent(html: string): Value {
  const editor = createSlateEditor({ plugins: emailBasePlugins });
  const nodes = deserializeHtml(editor, { element: html });
  return nodes.length > 0 ? (nodes as Value) : createEmptyValue();
}

export interface UseMessageEditorResult {
  editor: ReturnType<typeof usePlateEditor>;
  isEmpty: boolean;
  clearContent: () => void;
  getPlainText: () => string;
  setValue: (value: Value) => void;
  getSerializedValue: () => string;
  getHtml: () => Promise<string>;
}

export function useMessageEditor(
  mode: EditorToolbarMode = "email",
  id?: string,
): UseMessageEditorResult {
  const plugins = getPluginsForMode(mode);

  const editor = usePlateEditor({
    plugins,
    value: createEmptyValue(),
    id: id || `message-editor-${mode}`,
  });

  const isEmpty = editor.api.isEmpty();

  const clearContent = useHandler(() => {
    editor.tf.setValue(createEmptyValue());
  });

  const getPlainText = useHandler(() => {
    return editor.api.string([]);
  });

  const setValue = useHandler((value: Value) => {
    editor.tf.setValue(value);
  });

  const getSerializedValue = useHandler((): string => {
    return JSON.stringify(editor.children);
  });

  const getHtml = useHandler(async (): Promise<string> => {
    const staticEditor = createSlateEditor({
      plugins: getBasePluginsForMode(mode),
      value: editor.children,
    });
    return await serializeHtml(staticEditor, {
      stripClassNames: true,
      stripDataAttributes: true,
    });
  });

  return {
    editor,
    isEmpty,
    clearContent,
    getPlainText,
    setValue,
    getSerializedValue,
    getHtml,
  };
}
